import { useState } from 'react';

import { Button } from '../../shared/ui/button';
import { PlusIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Section } from '../../shared/ui/layout';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { OptionCard } from '../../shared/ui/option-card';
import { SectionPanel } from '../../shared/ui/section-panel';
import { Select } from '../../shared/ui/select';
import { Switch } from '../../shared/ui/switch';
import type { TAuthType } from '../../entities/destination';
import { Hint, Label, Problem } from './field-bits';
import type { IDestinationDraft, TDestinationSettings } from './use-destination-settings';

const AUTH: { value: TAuthType; label: string; description: string }[] = [
  { value: 'none', label: 'No credential', description: 'We send no authorization header' },
  { value: 'bearer', label: 'Bearer token', description: 'authorization: Bearer <token>' },
  { value: 'basic', label: 'Basic', description: 'authorization: Basic <user:password>' },
  { value: 'api_key', label: 'API key header', description: 'A header of your choosing' },
];

const CREDENTIAL_WORD: Record<TAuthType, string> = {
  none: 'credential',
  bearer: 'token',
  basic: 'password',
  api_key: 'API key',
};

const Toggle = ({
  settings,
  field,
  title,
  description,
}: {
  settings: TDestinationSettings;
  field: keyof Pick<IDestinationDraft, 'passThroughPath' | 'passThroughQuery' | 'followRedirects'>;
  title: string;
  description: string;
}) => (
  <OptionCard
    selected={settings.draft[field]}
    control={
      <Switch
        size="sm"
        aria-label={title}
        checked={settings.draft[field]}
        onCheckedChange={(next) => settings.change({ [field]: next })}
      />
    }
    title={title}
    description={description}
  />
);

const Credential = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, destination, problem } = settings;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const word = CREDENTIAL_WORD[draft.authType];

  const pending = Boolean(draft.authCredential);
  const stored = destination.authCredentialSet && draft.authCredential === undefined;
  const line = pending
    ? `A new ${word} — set when you save`
    : stored
      ? 'Set. It is never shown again'
      : `No ${word} yet — nothing is sent until there is one`;

  return (
    <div className="col-span-2">
      <Label>The {word}</Label>
      <span className="flex gap-2">
        <Input
          disabled
          mono
          aria-label={`The ${word}`}
          className="min-w-0 grow"
          value={line}
          readOnly
        />
        <Button variant="ghost" size="md" onClick={() => setOpen(true)}>
          {pending || stored ? 'Replace' : `Add the ${word}`}
        </Button>
      </span>
      <Problem>{problem('authCredential')}</Problem>
      <Hint>Kept apart from the signing key: replacing one never touches the other.</Hint>

      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setTyped('');
        }}
        title={pending || stored ? `Replace the ${word}` : `Add the ${word}`}
        description={`What we present to ${destination.name} on every delivery, from the next one after you save.`}
        submit={{
          label: 'Keep it',
          onSubmit: () => {
            change({ authCredential: typed.trim() });
            setOpen(false);
            setTyped('');
          },
          disabled: typed.trim().length === 0,
        }}
      >
        <Input
          mono
          autoFocus
          type="password"
          aria-label={`New ${word}`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
        <Note className="mt-3">
          Stored encrypted and never shown again. It goes only to this destination’s own host: a
          redirect elsewhere is followed without it.
        </Note>
      </Modal>
    </div>
  );
};

const ForwardedHeaders = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, problem } = settings;
  const set = (index: number, value: string) =>
    change({
      forwardHeaders: draft.forwardHeaders.map((name, at) => (at === index ? value : name)),
    });

  return (
    <SectionPanel
      title="Sender headers we pass on"
      actions={
        <Button
          variant="ghost"
          size="sm"
          className="h-control-xs gap-2"
          onClick={() => change({ forwardHeaders: [...draft.forwardHeaders, ''] })}
        >
          <PlusIcon size={12} /> Add
        </Button>
      }
    >
      {draft.forwardHeaders.length === 0 && (
        <p className="px-input py-3 text-meta text-muted">
          None. The sender’s own headers stay with us; name one here to pass it on by value.
        </p>
      )}
      {draft.forwardHeaders.map((name, index) => (
        <div
          key={index}
          className="flex h-row-lg items-center gap-2 border-b border-border-row px-input last:border-b-0"
        >
          <Input
            bare
            size="sm"
            mono
            aria-label="Sender header to pass on"
            placeholder="stripe-signature"
            className="min-w-0 grow px-0"
            value={name}
            onChange={(event) => set(index, event.target.value)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-control-xs shrink-0"
            onClick={() =>
              change({ forwardHeaders: draft.forwardHeaders.filter((_, at) => at !== index) })
            }
          >
            Remove
          </Button>
        </div>
      ))}
      {problem('forwardHeaders') && (
        <div className="px-input pb-3">
          <Problem>{problem('forwardHeaders')}</Problem>
        </div>
      )}
    </SectionPanel>
  );
};

export const DestinationCalling = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, problem } = settings;

  return (
    <Section
      title="How we call it"
      description="Everything about the request itself, apart from the headers listed below."
    >
      <div className="flex max-w-form-narrow flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>How we prove we may call it</Label>
            <Select
              aria-label="Authentication"
              value={draft.authType}
              options={AUTH}
              onChange={(authType) => change({ authType })}
            />
          </div>

          {draft.authType === 'basic' && (
            <div className="col-span-2">
              <Label>Username</Label>
              <Input
                aria-label="Username"
                invalid={Boolean(problem('authUsername'))}
                value={draft.authUsername}
                onChange={(event) => change({ authUsername: event.target.value })}
              />
              <Problem>{problem('authUsername')}</Problem>
            </div>
          )}

          {draft.authType === 'api_key' && (
            <>
              <div>
                <Label>Header</Label>
                <Input
                  mono
                  aria-label="Header"
                  placeholder="x-api-key"
                  invalid={Boolean(problem('authHeader'))}
                  value={draft.authHeader}
                  onChange={(event) => change({ authHeader: event.target.value })}
                />
                <Problem>{problem('authHeader')}</Problem>
              </div>
              <div>
                <Label>Before the key</Label>
                <Input
                  mono
                  aria-label="Before the key"
                  placeholder="nothing — e.g. Token "
                  value={draft.authPrefix}
                  onChange={(event) => change({ authPrefix: event.target.value })}
                />
              </div>
            </>
          )}

          {draft.authType !== 'none' && <Credential settings={settings} />}
        </div>

        <ForwardedHeaders settings={settings} />

        <Toggle
          settings={settings}
          field="passThroughPath"
          title="Pass the path on"
          description="A request to …/in/src_x/orders/7 is delivered to this URL plus /orders/7."
        />
        <Toggle
          settings={settings}
          field="passThroughQuery"
          title="Pass the query string on"
          description="?a=1 on the way in is appended to this URL on the way out."
        />
        <Toggle
          settings={settings}
          field="followRedirects"
          title="Follow redirects"
          description={
            draft.followRedirects
              ? 'Up to 3 hops, resending the same method and body. The credential is dropped when a hop leaves this host; the signature is not.'
              : 'Off: a 3xx answer is a failure, and it is not retried — the address will not change by itself.'
          }
        />
      </div>
    </Section>
  );
};
